import React, { useMemo, useRef, useState } from 'react';

import { MapTo } from '@adobe/aem-react-editable-components';
import { useMutation } from '@apollo/client';
import { VALIDATE_PARTICIPATION_CODE_MUTATION } from 'Queries/Mutations';
import { useFormik } from 'formik';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormSection } from 'components/FormHelper/FormSection';

import { VALIDATION_CONSTANTS } from 'global/constants';

import './ParticipationCodeEntry.sass';

const validationSchema = Yup.object().shape({
  participationCode: Yup.string()
    .matches(/^\d{6}$/, 'Der Code muss aus 6 Ziffern bestehen')
    .required(VALIDATION_CONSTANTS.MANDATORY),
});

const INITIAL_VALUES = {
  participationCode: '',
};

const CODE_INPUT_INDEXES = [0, 1, 2, 3, 4, 5];

const hasAlreadyAttendedError = (error) =>
  error?.message?.includes('ALREADY_ATTENDED') ||
  error?.graphQLErrors?.some(({ message }) => message?.includes('ALREADY_ATTENDED'));

export const ParticipationCodeEntry = () => {
  const { search } = useLocation();
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const eventId = useMemo(() => {
    const parsedQS = qs.parse(search);
    return parsedQS.eventId || null;
  }, [search]);

  const [validateParticipationCode] = useMutation(VALIDATE_PARTICIPATION_CODE_MUTATION, {
    onCompleted: (data) => {
      if (data?.validateAttendance?.success) {
        setSubmitStatus('success');
        setErrorMessage('');
      } else {
        setSubmitStatus('error');
        setErrorMessage('Ungültiger Code.');
      }
    },
    onError: (error) => {
      setSubmitStatus('error');
      if (hasAlreadyAttendedError(error)) {
        setErrorMessage(
          'Ihre Anwesenheit wurde bereits erfasst. Sie können die Seite nun verlassen.'
        );
      } else if (error.networkError) {
        setErrorMessage('Netzwerkfehler. Bitte versuchen Sie es erneut.');
      } else {
        setErrorMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    },
  });
  const handleSubmit = async (values) => {
    if (!eventId) {
      setSubmitStatus('error');
      setErrorMessage('Event-ID nicht gefunden.');
      return;
    }

    setSubmitStatus('submitting');
    setErrorMessage('');

    try {
      await validateParticipationCode({
        variables: {
          eventId: Number(eventId),
          participationCode: values.participationCode,
        },
      });
    } catch (error) {
      console.error('Error validating participation code:', error);
    }
  };
  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
  });

  const clearSubmitError = () => {
    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const handleInputChange = (index, value) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const currentCode = formik.values.participationCode;
    const codeArray = currentCode.split('').concat(['', '', '', '', '', '']);
    codeArray[index] = value;

    const newCode = codeArray.slice(0, 6).join('');
    formik.setFieldValue('participationCode', newCode);
    clearSubmitError();

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      const currentCode = formik.values.participationCode;
      const codeArray = currentCode.split('').concat(['', '', '', '', '', '']);

      if (!codeArray[index] && index > 0) {
        inputRefs[index - 1].current?.focus();
      } else {
        codeArray[index] = '';
        const newCode = codeArray.slice(0, 6).join('');
        formik.setFieldValue('participationCode', newCode);
        clearSubmitError();
      }
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    setSubmitStatus(null);
    setErrorMessage('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    formik.setFieldValue('participationCode', digits);
    clearSubmitError();
    const focusIndex = Math.min(digits.length, 5);
    inputRefs[focusIndex].current?.focus();
  };

  const getCodeDigits = useMemo(() => {
    const code = formik.values.participationCode || '';
    const digits = code.split('');
    while (digits.length < 6) {
      digits.push('');
    }
    return digits;
  }, [formik.values.participationCode]);

  const getInputClassName = () => {
    let className = 'participation-code-entry__input';

    if (submitStatus === 'error' || (formik.errors.participationCode && formik.submitCount > 0)) {
      className += ' participation-code-entry__input--error';
    } else if (submitStatus === 'success') {
      className += ' participation-code-entry__input--success';
    }

    return className;
  };

  const hasErrors = !!errorMessage;

  return (
    <div className="participation-code-entry base-tenant-style">
      <div className="participation-code-entry__card">
        <h2 className="participation-code-entry__title">Teilnahme-Code eingeben</h2>
        <p className="participation-code-entry__description">
          Geben Sie hier Ihren per Mail erhaltenen Teilnahme-Code ein.
        </p>

        <FormSection>
          <form onSubmit={formik.handleSubmit}>
            <div className="participation-code-entry__inputs">
              {CODE_INPUT_INDEXES.map((inputIndex) => (
                <input
                  key={`participation-code-input-${inputIndex}`}
                  ref={inputRefs[inputIndex]}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={getCodeDigits[inputIndex]}
                  onChange={(e) => handleInputChange(inputIndex, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(inputIndex, e)}
                  onPaste={handlePaste}
                  className={getInputClassName()}
                  disabled={submitStatus === 'submitting' || submitStatus === 'success'}
                  data-testid={`participation-code-input-${inputIndex}`}
                />
              ))}
            </div>

            {submitStatus === 'success' && (
              <div className="participation-code-entry__status participation-code-entry__status--success">
                <div>
                  <span className="success-icon">✓</span>
                  Code erfasst
                </div>
                <div className="participation-code-entry__status--success-message">
                  Ihre Anwesenheit wurde erfasst. Sie können die Seite nun verlassen.
                </div>
              </div>
            )}

            {submitStatus === 'error' && errorMessage && (
              <div className="participation-code-entry__status participation-code-entry__status--error">
                {errorMessage}
              </div>
            )}

            {formik.errors.participationCode &&
              formik.submitCount > 0 &&
              submitStatus !== 'error' &&
              submitStatus !== 'success' && (
                <div className="participation-code-entry__status participation-code-entry__status--error">
                  {formik.errors.participationCode}
                </div>
              )}

            {submitStatus !== 'success' && (
              <FormActionButtons
                loading={submitStatus === 'submitting'}
                hasErrors={hasErrors}
                titleLeft="Abbrechen"
                titleRight="Code prüfen"
                onClickLeft={handleCancel}
                onClickRight={formik.handleSubmit}
                errorMessage=""
              />
            )}
          </form>
        </FormSection>
      </div>
    </div>
  );
};

ParticipationCodeEntry.displayName = 'ParticipationCodeEntry';

MapTo(['e-fix/components/content/participationcodeentry'])(ParticipationCodeEntry);
